import { Injectable, NgZone } from '@angular/core';
import { SupabaseClient, createClient } from '@supabase/supabase-js';
import { Database } from '../../models/types';
import { environment } from 'src/environments/environment';
import { NetworkErrorService } from '../network-error.service';

@Injectable({
  providedIn: 'root',
})
export class SupabaseService {
  private supabaseUrl: string;
  private supabaseKey: string;
  public supabase: SupabaseClient<Database>;
  
  constructor(
    private networkErrorService: NetworkErrorService,
    private zone: NgZone
  ) {
    this.supabaseUrl = environment.supabaseUrl;
    this.supabaseKey = environment.supabaseKey;

    const customFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const response = await fetch(input, init);
        
        // Check for server errors (500-599)
        if (response.status >= 500) {
          this.zone.run(() => {
            this.networkErrorService.showGenericError();
          });
        }
        
        return response;
      } catch (error) {
        // Network errors (e.g. offline, DNS failure)
        this.zone.run(() => {
          this.networkErrorService.showGenericError();
        });
        throw error;
      }
    };

    this.supabase = createClient<Database>(this.supabaseUrl, this.supabaseKey, {
      global: {
        fetch: customFetch
      }
    });
  }

  /**
   * POST/GET a lambda endpoint with the current session's access token, and unwrap
   * retSuccess()'s { message, details } envelope. For data-access functions (e.g. in
   * cocounsel/claims-v2/) that take a SupabaseService but have no HttpClient of their own —
   * mirrors the token-fetch pattern already used by DatabaseService.callLambda / SotService.
   */
  async callLambda(path: string, method: 'GET' | 'POST' = 'POST', body?: any): Promise<any> {
    const token = (await this.supabase.auth.getSession()).data.session?.access_token;
    if (!token) throw new Error(`callLambda(${path}): no auth session`);

    const base = (environment.lambdaUrl || '').replace(/\/$/, '');
    const resp = await fetch(`${base}/${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!resp.ok) {
      // CPK-4785: the thrown Error keeps its old message, and now also CARRIES the status and the
      // parsed body. A 409 from the dismissal endpoint exists to say WHY it refused ("this PoA has
      // a signed contract"); throwing only "HTTP 409" turned the one message the office needs into
      // a number. Additive on purpose — callers that read only `.message` are unchanged.
      const err = new Error(`callLambda(${path}) failed: HTTP ${resp.status}`) as LambdaError;
      err.status = resp.status;
      err.body = await resp.json().catch(() => null);
      throw err;
    }

    const json = await resp.json();
    return json?.details ?? json ?? {};
  }
}

/** Erro de `callLambda` com o que a resposta dizia, para quem precisa do corpo e não só do código. */
export interface LambdaError extends Error {
  status?: number;
  body?: { message?: string; details?: Record<string, unknown> } | null;
}
